<script>
  import Header from './components/Header.svelte';
  import Navigation from './components/Navigation.svelte';
  import ErrorBoundary from './components/ErrorBoundary.svelte';
  import CookieConsent from './components/CookieConsent.svelte';
  import ItemCatalog from './components/features/ItemCatalog.svelte';
  import { currentRoute, navigate } from './routes.js';
  import { t } from './services/i18n.js';
  import { env } from './config/env.js';

  let showCookieConsent = $state(false);

  $effect(() => {
    // Check if user has already made a cookie choice
    const cookieChoice = localStorage.getItem('cookie-consent');
    if (!cookieChoice) {
      showCookieConsent = true;
    }
  });

  function handleCookieConsent(accepted) {
    localStorage.setItem('cookie-consent', accepted ? 'accepted' : 'declined');
    showCookieConsent = false;
  }
</script>

<ErrorBoundary>
  <div class="app-container">
    <Header />
    
    <Navigation />
    
    <main class="main-content">
      {#if $currentRoute === 'home' || $currentRoute === 'catalog'}
        <ItemCatalog />
      {:else if $currentRoute === 'about'}
        <section class="page-section about-page">
          <h1>{$t('about.title')}</h1>
          
          <div class="about-content">
            <div class="about-intro">
              <p>{$t('about.description1')}</p>
              <p>{$t('about.description2')}</p>
              <p>{$t('about.description3')}</p>
              <p class="about-cta">{$t('about.description4')}</p>
            </div>

            <div class="about-values">
              <h2>{$t('about.ourValues')}</h2>
              <ul>
                <li>
                  <strong>💜 {$t('about.curation')}</strong>
                  <p>{$t('about.curationDesc')}</p>
                </li>
                <li>
                  <strong>🐈‍⬛ {$t('about.exclusivity')}</strong>
                  <p>{$t('about.exclusivityDesc')}</p>
                </li>
                <li>
                  <strong>♻️ {$t('about.consumption')}</strong>
                  <p>{$t('about.consumptionDesc')}</p>
                </li>
              </ul>
            </div>
          </div>
        </section>
      {:else if $currentRoute === 'contact'}
        <section class="page-section contact-page">
          <div class="linktree-container">
            <div class="linktree-header">
              <h1>
                {$t('contact.title')} 🐈‍⬛
              </h1>
              <p>{$t('contact.description')}</p>
            </div>
            
            <div class="linktree-links">
              <!-- Main Contact Links -->
              <a 
                href="https://wa.me/{env.whatsappNumber.replace(/\D/g, '')}?text={encodeURIComponent($t('contact.whatsappMessage'))}"
                target="_blank"
                rel="noopener noreferrer"
                class="linktree-btn primary"
              >
                {$t('contact.whatsappButton')}
              </a>

              <!-- Social Links -->
              {#if env.instagramUrl}
                <a 
                  href={env.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="linktree-btn primary"
                  aria-label="Instagram"
                >
                  Instagram
                </a>
              {/if}

              {#if env.tiktokUrl}
                <a 
                  href={env.tiktokUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="linktree-btn primary"
                  aria-label="TikTok"
                >
                  TikTok
                </a>
              {/if}

              {#if env.youtubeUrl}
                <a 
                  href={env.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="linktree-btn primary"
                  aria-label="YouTube"
                >
                  YouTube
                </a>
              {/if}

              {#if env.pinterestUrl}
                <a 
                  href={env.pinterestUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="linktree-btn primary"
                  aria-label="Pinterest"
                >
                  Pinterest
                </a>
              {/if}
              
              {#if env.contactEmail}
                <a 
                  href="mailto:{env.contactEmail}?subject={encodeURIComponent($t('contact.emailSubject'))}&body={encodeURIComponent($t('contact.emailBody'))}"
                  class="linktree-btn primary"
                >
                  {$t('contact.emailButton')}
                </a>
              {/if}
            </div>
          </div>
        </section>
      {:else}
        <section class="page-section">
          <h1>{$t('notFound.title')}</h1>
          <p>{$t('notFound.description')}</p>
          <button onclick={() => navigate('home')} class="btn-primary">
            {$t('notFound.backHome')}
          </button>
        </section>
      {/if}
    </main>

    <footer class="app-footer">
      <div class="footer-content">
        <p>&copy; 2025 Baú da Gata. {$t('footer.rights')}</p>
        <nav class="footer-links">
          <a href="#privacy" onclick={(e) => { e.preventDefault(); navigate('privacy'); }}>
            {$t('footer.privacy')}
          </a>
          <a href="#terms" onclick={(e) => { e.preventDefault(); navigate('terms'); }}>
            {$t('footer.terms')}
          </a>
        </nav>
      </div>
    </footer>
  </div>

  {#if showCookieConsent}
    <CookieConsent 
      onAccept={() => handleCookieConsent(true)}
      onDecline={() => handleCookieConsent(false)}
    />
  {/if}
</ErrorBoundary>

<style>
  .app-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: var(--color-background);
    color: var(--color-text);
  }

  .main-content {
    flex: 1;
    padding: var(--spacing-lg);
    max-width: var(--max-width);
    margin: 0 auto;
    width: 100%;
  }

  .page-section {
    padding: var(--spacing-xl) 0;
  }

  .page-section h1 {
    color: var(--color-primary);
    margin-bottom: var(--spacing-md);
  }

  /* About Page Styles */
  .about-page {
    max-width: 800px;
    margin: 0 auto;
  }

  .about-content {
    display: flex;
    flex-direction: column;
    gap: 3rem;
  }

  .about-intro p {
    line-height: 1.8;
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
    text-indent: 2rem;
  }

  .about-intro p.about-cta {
    font-weight: 700;
    font-size: 1.4rem;
    color: var(--color-primary);
    margin-top: 2.5rem;
    text-align: center;
    text-indent: 0;
    letter-spacing: 0.5px;
  }

  .about-intro h2,
  .about-values h2 {
    color: var(--color-primary);
    margin-bottom: 1rem;
    font-size: 1.75rem;
  }

  .about-values ul {
    list-style: none;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .about-values li {
    background: var(--color-surface);
    padding: 1.5rem;
    border-radius: 8px;
    border-left: 4px solid var(--color-primary);
  }

  .about-values li strong {
    display: block;
    font-size: 1.2rem;
    margin-bottom: 0.5rem;
    color: var(--color-primary);
  }

  .about-values li p {
    margin: 0;
    line-height: 1.6;
  }

  /* Contact Page Styles - LinkTree Inspired */
  .contact-page {
    max-width: 680px;
    margin: 0 auto;
    padding: 2rem 1rem;
  }

  .linktree-container {
    width: 100%;
  }

  .linktree-header {
    text-align: center;
    margin-bottom: 2.5rem;
  }

  .linktree-header h1 {
    font-size: 2rem;
    margin-bottom: 0.75rem;
    color: var(--color-primary);
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
  }

  .title-icon {
    height: 2rem;
    width: auto;
    object-fit: contain;
  }

  .linktree-header p {
    font-size: 1rem;
    color: var(--color-text-secondary);
    line-height: 1.6;
  }

  .linktree-links {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 100%;
  }

  .linktree-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.25rem 2rem;
    border-radius: 12px;
    text-decoration: none;
    font-size: 1.05rem;
    font-weight: 600;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
    width: 100%;
    position: relative;
    overflow: hidden;
    text-align: center;
  }

  /* Primary Button - Purple */
  .linktree-btn.primary {
    background: var(--color-primary);
    color: white;
    border: 2px solid var(--color-primary);
  }

  :global(html[data-theme="dark"]) .linktree-btn.primary {
    background: white;
    color: #732e83;
    border-color: #732e83;
  }

  .linktree-btn.primary:hover {
    background: white;
    color: var(--color-primary);
    border-color: var(--color-primary);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(115, 46, 131, 0.3);
  }

  :global(html[data-theme="dark"]) .linktree-btn.primary:hover {
    background: #732e83;
    color: white;
    border-color: #732e83;
  }

  /* Secondary Button - Gold */
  .linktree-btn.secondary {
    background: var(--color-secondary);
    color: var(--color-primary);
    border: 2px solid var(--color-secondary);
  }

  .linktree-btn.secondary:hover {
    background: #b89554;
    border-color: #b89554;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(198, 166, 100, 0.3);
  }

  /* Outline Buttons - Lavender */
  .linktree-btn.outline {
    background: transparent;
    color: var(--color-primary);
    border: 2px solid var(--color-primary-light);
  }

  .linktree-btn.outline:hover {
    background: var(--color-primary-light);
    border-color: var(--color-primary-light);
    color: var(--color-primary);
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(216, 199, 243, 0.4);
  }

  @media (max-width: 768px) {
    .contact-page {
      padding: 1.5rem 1rem;
    }

    .linktree-header h1 {
      font-size: 1.75rem;
    }

    .linktree-btn {
      padding: 1.1rem 1.5rem;
      font-size: 1rem;
    }
  }

  .btn-primary {
    background-color: var(--color-primary);
    color: white;
    padding: var(--spacing-sm) var(--spacing-lg);
    border: none;
    border-radius: var(--border-radius);
    cursor: pointer;
    font-size: 1rem;
    transition: background-color var(--transition-fast);
  }

  .btn-primary:hover {
    background-color: var(--color-primary-dark);
  }

  .app-footer {
    background-color: var(--color-surface);
    border-top: 1px solid var(--color-border);
    padding: var(--spacing-lg);
    margin-top: auto;
  }

  .footer-content {
    max-width: var(--max-width);
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: var(--spacing-md);
  }

  .footer-links {
    display: flex;
    gap: var(--spacing-md);
  }

  .footer-links a {
    color: var(--color-text-secondary);
    text-decoration: none;
    transition: color var(--transition-fast);
  }

  .footer-links a:hover {
    color: var(--color-primary);
  }

  @media (max-width: 768px) {
    .main-content {
      padding: var(--spacing-md);
    }

    .footer-content {
      flex-direction: column;
      text-align: center;
    }
  }
</style>
